import {
  CheckCircle2,
  Clock3,
  MapPin,
  MessageCircle,
  PackageCheck,
  ShieldCheck,
  Truck,
} from "lucide-react";
import { cashDueForOrder } from "../lib/localErrands.js";
import { orderStatusLabel, travelerOrderAction } from "../lib/orderPresentation.js";

export default function OrderCard({
  order,
  role,
  userId,
  variant = "a",
  buyerProfile,
  buyerProfilesLoading,
  travelerProfile,
  travelerProfilesLoading,
  contactTools,
  canReviewParticipant,
  onOpenReviews,
  dispute,
  statusLabels,
  canReportIssue,
  onAuthorize,
  onCancel,
  onDecline,
  onAdvance,
  onReportIssue,
  onOpenChat,
}) {
  const action = travelerOrderAction(order.status);
  const statusIndex = statusLabels.indexOf(order.status);
  const participantProfile = role === "buyer" ? travelerProfile : buyerProfile;
  const participantProfilesLoading =
    role === "buyer" ? travelerProfilesLoading : buyerProfilesLoading;
  const participantRole = role === "buyer" ? "Assigned traveler" : "Buyer";
  const participantFallback = role === "buyer" ? "Traveler" : "Buyer";
  const formattedDate = order.created_at
    ? new Date(order.created_at).toLocaleDateString("en-PH", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : null;
  const shortId = String(order.id || "")
    .replace(/-/g, "")
    .slice(0, 6)
    .toUpperCase();
  const trackerStatuses = statusLabels.slice(0, 5);
  const trackerIcons = [Clock3, CheckCircle2, PackageCheck, Truck, ShieldCheck];
  const itemBudget = Number(order.budget).toLocaleString("en-PH", {
    minimumFractionDigits: 2,
  });
  const displayBudget = order.local_offer_id
    ? cashDueForOrder(order).toLocaleString("en-PH", {
        minimumFractionDigits: 2,
      })
    : itemBudget;

  return (
    <article className={`order-card order-card--${variant}`}>
      <header className="order-card__header">
        <div className="order-card__meta">
          <span className="order-card__status">
            <span aria-hidden="true" />
            {orderStatusLabel(order.status)}
          </span>
          {shortId && <span>ID: {shortId}</span>}
          {formattedDate && (
            <time dateTime={order.created_at}>
              <Clock3 size={15} aria-hidden="true" />
              {formattedDate}
            </time>
          )}
        </div>
        <div className="order-card__budget">
          <span>{order.local_offer_id ? "Cash due" : "Item budget"}</span>
          <strong>PHP {displayBudget}</strong>
        </div>
      </header>

      <div className="order-card__body">
        <div className="order-card__summary">
          <span className="order-card__kind">
            {order.local_offer_id ? "Local errand" : "Trip request"}
          </span>
          <h3>{order.item_name}</h3>
          <p className="order-card__request-note">
            {order.local_offer_id
              ? "Local errand purchase requested from this traveler."
              : "Item request from a traveler trip."}
          </p>
          <div className="order-card__location">
            <MapPin size={16} aria-hidden="true" />
            <span>{order.delivery_city || "Delivery area not provided"}</span>
          </div>

          <div className="order-buyer-profile">
            {participantProfile?.photoUrl ? (
              <img src={participantProfile.photoUrl} alt="" />
            ) : (
              <span className="order-buyer-avatar-placeholder" aria-hidden="true">
                {participantProfile?.display_name?.trim()?.slice(0, 1)?.toUpperCase() ||
                  participantFallback.slice(0, 1)}
              </span>
            )}
            <div className="order-buyer-main">
              <span className="order-buyer-label">{participantRole}</span>
              <strong>
                {participantProfile?.display_name ||
                  (participantProfilesLoading
                    ? `Loading ${participantFallback.toLowerCase()}…`
                    : `${participantFallback} profile unavailable`)}
              </strong>
              {participantProfile?.public_profile_id ? (
                <a href={`/p/${participantProfile.public_profile_id}`}>
                  View {participantFallback.toLowerCase()} profile
                </a>
              ) : participantProfile ? (
                <span className="order-buyer-private">
                  {participantProfile.is_public
                    ? "Profile link unavailable"
                    : "Profile is private"}
                </span>
              ) : null}
              {participantProfile?.photoError && (
                <small>Profile photo could not be loaded.</small>
              )}
            </div>
            {canReviewParticipant && (
              <button
                type="button"
                className="secondary review-profile-link"
                onClick={onOpenReviews}
              >
                View reviews
              </button>
            )}
            {contactTools && (
              <div className="order-buyer-contact-tools">{contactTools}</div>
            )}
          </div>
        </div>

        <section className="order-card__tracker" aria-label="Delivery progress">
          <div className="order-card__tracker-heading">
            <span>Delivery progress</span>
            <strong>
              Step {Math.min(Math.max(statusIndex + 1, 1), 5)} of 5
            </strong>
          </div>
          <ol>
            {trackerStatuses.map((status, index) => {
              const Icon = trackerIcons[index];
              const isReached = statusIndex >= index;
              return (
                <li
                  className={`${isReached ? "is-reached" : ""} ${
                    statusIndex === index ? "is-current" : ""
                  }`}
                  key={status}
                >
                  <span className="order-card__tracker-icon" aria-hidden="true">
                    <Icon size={16} />
                  </span>
                  <span>{orderStatusLabel(status)}</span>
                </li>
              );
            })}
          </ol>
          <div className="progress" aria-hidden="true">
            {trackerStatuses.map((status, index) => (
              <span
                className={statusIndex >= index ? "done" : ""}
                key={status}
              />
            ))}
          </div>
        </section>
      </div>

      {dispute && (
        <p className="dispute-status" role="status">
          Issue report: {dispute.status.replace(/_/g, " ")}
        </p>
      )}

      <footer>
        <div className="order-card__secondary-actions">
          {role === "buyer" && order.payment_state === "pending" && (
            <button type="button" onClick={onAuthorize}>
              <CheckCircle2 size={16} />
              Authorize
            </button>
          )}
          {role === "buyer" && order.status === "requested" && (
            <button type="button" className="secondary" onClick={onCancel}>
              Cancel request
            </button>
          )}
          {role === "traveler" &&
            order.traveler_id === userId &&
            order.status === "requested" && (
              <button type="button" className="secondary" onClick={onDecline}>
                Decline request
              </button>
            )}
          {canReportIssue && (
            <button type="button" className="secondary" onClick={onReportIssue}>
              Report an issue
            </button>
          )}
        </div>
        <div className="order-card__primary-actions">
          {role === "traveler" &&
            order.traveler_id === userId &&
            action && (
              <button type="button" onClick={onAdvance}>
                {action.label}
              </button>
            )}
          <button type="button" className="order-card__chat" onClick={onOpenChat}>
            <MessageCircle size={16} aria-hidden="true" />
            Open chat
          </button>
        </div>
      </footer>
    </article>
  );
}
